import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Avatar
} from '@mui/material';
import {
  Calendar,
  Clock,
  Check,
  X,
  WarningCircle,
  CalendarCheck
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { format, parseISO, isToday, isTomorrow, isPast, isFuture } from 'date-fns';
import useThemeStyles from '../../theme/useThemeStyles';

const CouplesBookings = () => {
  const styles = useThemeStyles();
  const { _user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [_adjustmentModalOpen, _setAdjustmentModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rejectRescheduleModalOpen, setRejectRescheduleModalOpen] = useState(false);
  const [suggestedTimes, setSuggestedTimes] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/unified-bookings/my-bookings');
      setBookings(response.data.bookings || []);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Please log in to view your bookings');
      } else {
        setError('Failed to load bookings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setBookingModalOpen(true);
  };

  const handleApproveAdjustment = async () => {
    try {
      await api.post(`/api/unified-bookings/${selectedBooking._id}/respond-reschedule`, {
        action: 'approve'
      });
      fetchBookings();
      setBookingModalOpen(false);
      setSelectedBooking(null);
    } catch (error) {}
  };

  const handleRejectAdjustment = async () => {
    try {
      await api.post(`/api/unified-bookings/${selectedBooking._id}/respond-reschedule`, {
        action: 'reject',
        ...(suggestedTimes.trim() && { suggestedTimes: suggestedTimes.trim() })
      });
      fetchBookings();
      setBookingModalOpen(false);
      setRejectRescheduleModalOpen(false);
      setSelectedBooking(null);
      setSuggestedTimes('');
    } catch (error) {}
  };

  const handleRequestCancel = async () => {
    try {
      await api.put(`/api/unified-bookings/${selectedBooking._id}/status`, {
        status: 'cancelled',
        notes: cancelReason,
        notifyClient: false
      });
      
      fetchBookings();
      setCancelModalOpen(false);
      setBookingModalOpen(false);
      setSelectedBooking(null);
      setCancelReason('');
    } catch (error) {}
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'rescheduled': return 'info';
      case 'cancelled': return 'error';
      case 'rejected': return 'error';
      case 'completed': return 'default';
      case 'supplier_needs_to_respond': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <Check size={16} />;
      case 'pending': return <Clock size={16} />;
      case 'rescheduled': return <WarningCircle size={16} />;
      case 'cancelled': return <X size={16} />;
      case 'rejected': return <X size={16} />;
      case 'completed': return <Check size={16} />;
      case 'supplier_needs_to_respond': return <WarningCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const formatDate = (dateString) => {
    const date = parseISO(dateString);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEEE, MMMM do, yyyy');
  };

  const formatTime = (dateString) => {
    return format(parseISO(dateString), 'h:mm a');
  };

  const getDisplayStatus = (booking) => {
    // If it's a pending booking that's now in the past, show "supplier_needs_to_respond"
    if (booking.status === 'pending' && isPast(parseISO(booking.startTime))) {
      return 'supplier_needs_to_respond';
    }
    return booking.status;
  };

  const getRescheduleLabel = (booking) => {
    if (booking.status !== 'rescheduled') return null;
    return booking.requiresClientConfirmation ? 'Reschedule Request' : 'New time confirmed';
  };

  const getFilteredBookings = () => {
    switch (activeTab) {
      case 0: // All
        return bookings;
      case 1: // Upcoming
        return bookings.filter(b => isFuture(parseISO(b.startTime)) && b.status !== 'cancelled');
      case 2: // Pending
        return bookings.filter(b => {
          const displayStatus = getDisplayStatus(b);
          return displayStatus === 'pending' || displayStatus === 'rescheduled' || displayStatus === 'supplier_needs_to_respond';
        });
      case 3: // Past
        return bookings.filter(b => isPast(parseISO(b.startTime)) && b.status !== 'cancelled');
      default:
        return bookings;
    }
  };

  const renderBookingList = () => {
    const filteredBookings = getFilteredBookings();
    
    if (filteredBookings.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Calendar size={48} color="#ccc" />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            No bookings found
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {filteredBookings.map((booking) => (
          <ListItem
            key={booking._id}
            sx={{
              flexDirection: 'column',
              alignItems: 'stretch',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: booking.status === 'rescheduled' && booking.requiresClientConfirmation ? 'error.main' : booking.status === 'rescheduled' ? 'success.main' : 'divider',
              borderWidth: booking.status === 'rescheduled' ? 2 : 1,
              borderRadius: 1,
              mb: 1,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2, flexShrink: 0 }}>
                {getStatusIcon(getDisplayStatus(booking))}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {booking.serviceType?.name}
                  </Typography>
                  <Chip
                    label={getDisplayStatus(booking) === 'supplier_needs_to_respond' ? 'Supplier needs to respond' : getRescheduleLabel(booking) || getDisplayStatus(booking)}
                    color={booking.status === 'rescheduled' && !booking.requiresClientConfirmation ? 'success' : getStatusColor(getDisplayStatus(booking))}
                    size="small"
                    icon={getStatusIcon(getDisplayStatus(booking))}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>{booking.business?.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(booking.startTime)} at {formatTime(booking.startTime)}
                </Typography>
                {booking.status === 'rescheduled' && booking.requiresClientConfirmation && (
                  <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                    <strong>Reschedule Request:</strong> {booking.rescheduleReason}
                  </Alert>
                )}
                {booking.status === 'rescheduled' && !booking.requiresClientConfirmation && (
                  <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                    You&apos;ve accepted the new time for this booking.
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleViewBooking(booking)}
                  sx={{ mt: 1 }}
                >
                  View Details
                </Button>
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderBookingModal = () => (
    <Dialog
      open={bookingModalOpen}
      onClose={() => setBookingModalOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalendarCheck size={24} />
          <Typography variant="h6">Booking Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedBooking && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Service
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedBooking.serviceType?.name}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Business
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedBooking.business?.name}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Date & Time
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(selectedBooking.startTime)} at {formatTime(selectedBooking.startTime)}
                </Typography>
                
                {selectedBooking.status === 'rescheduled' && selectedBooking.requiresClientConfirmation && selectedBooking.originalStartTime && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                      Original Date & Time
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDate(selectedBooking.originalStartTime)} at {formatTime(selectedBooking.originalStartTime)}
                    </Typography>
                  </>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={selectedBooking.status === 'rescheduled' && !selectedBooking.requiresClientConfirmation ? 'New time confirmed' : selectedBooking.status}
                  color={selectedBooking.status === 'rescheduled' && !selectedBooking.requiresClientConfirmation ? 'success' : getStatusColor(selectedBooking.status)}
                  icon={getStatusIcon(selectedBooking.status)}
                  sx={{ mb: 2 }}
                />
                
                {selectedBooking.status === 'rescheduled' && selectedBooking.requiresClientConfirmation && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Reschedule Reason
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedBooking.rescheduleReason}
                    </Typography>
                  </>
                )}
                {selectedBooking.status === 'rescheduled' && !selectedBooking.requiresClientConfirmation && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    You&apos;ve accepted the new time. Your booking is confirmed for the date shown above.
                  </Alert>
                )}
                
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {selectedBooking?.status === 'rescheduled' && selectedBooking?.requiresClientConfirmation && (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setRejectRescheduleModalOpen(true)}
            >
              Keep Original Time
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApproveAdjustment}
            >
              Accept New Time
            </Button>
          </>
        )}
        
        {selectedBooking &&
          (selectedBooking.status === 'confirmed' || selectedBooking.status === 'rescheduled') &&
          isFuture(parseISO(selectedBooking.startTime)) && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setCancelModalOpen(true)}
          >
            Request Cancellation
          </Button>
        )}
        
        <Button onClick={() => setBookingModalOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCancelModal = () => (
    <Dialog
      open={cancelModalOpen}
      onClose={() => setCancelModalOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Request Cancellation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please provide a reason for cancelling this booking. The supplier will be notified.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Cancellation Reason"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Please explain why you need to cancel this booking..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCancelModalOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleRequestCancel}
          disabled={!cancelReason.trim()}
        >
          Request Cancellation
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderRejectRescheduleModal = () => (
    <Dialog
      open={rejectRescheduleModalOpen}
      onClose={() => {
        setRejectRescheduleModalOpen(false);
        setSuggestedTimes('');
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Keep Original Time</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The supplier will be notified that you&apos;d like to keep the original booking time.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Suggest some new times"
          value={suggestedTimes}
          onChange={(e) => setSuggestedTimes(e.target.value)}
          placeholder="e.g. Weekday evenings, Saturday mornings, or specific dates that work for you..."
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setRejectRescheduleModalOpen(false);
            setSuggestedTimes('');
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleRejectAdjustment}
        >
          Keep Original Time
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          My Bookings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your appointments and respond to reschedule requests
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab label={`All (${bookings.length})`} />
          <Tab label={`Upcoming (${bookings.filter(b => isFuture(parseISO(b.startTime)) && b.status !== 'cancelled').length})`} />
          <Tab label={`Pending (${bookings.filter(b => {
            const displayStatus = getDisplayStatus(b);
            return displayStatus === 'pending' || displayStatus === 'rescheduled' || displayStatus === 'supplier_needs_to_respond';
          }).length})`} />
          <Tab label={`Past (${bookings.filter(b => isPast(parseISO(b.startTime)) && b.status !== 'cancelled').length})`} />
        </Tabs>
      </Paper>

      {renderBookingList()}

      {renderBookingModal()}
      {renderCancelModal()}
      {renderRejectRescheduleModal()}
    </Container>
  );
};

export default CouplesBookings;
